import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';

import type { IFeedbackSite } from '@app/interfaces/feedback-site.interface';
import {
  NEW_FEEDBACK_SITE,
  NewFeedbackSiteFormConfig,
} from '@app/constants/feedback-site';
import useForm from '@app/hooks/useForm';
import {
  addFeedbackSite,
  updateFeedbackSite,
} from '@app/api/feedback-site';

type Props = {
  feedbackSite?: IFeedbackSite;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSuccess: () => void;
};

function AddFeedbackSiteButton({
  feedbackSite = NEW_FEEDBACK_SITE,
  isOpen,
  onOpen,
  onClose,
  onSuccess,
}: Props) {
  const { item, setItem, changeInput, changeItem } =
    useForm<typeof NEW_FEEDBACK_SITE>(feedbackSite);
  const [isImageError, setIsImageError] = React.useState(false);
  const imageUrl = item.imageUrl;

  React.useEffect(() => {
    setItem(feedbackSite);
  }, [feedbackSite, setItem]);

  React.useEffect(() => {
    setIsImageError(false);
  }, [imageUrl]);

  const onAddButtonClick = async () => {
    if (item?.id) {
      await updateFeedbackSite(item);
    } else {
      await addFeedbackSite(item);
    }

    onSuccess();
    onClose();
  };

  const isValidForm = () => {
    return NewFeedbackSiteFormConfig.filter(({ required }) => required).every(({ field }) =>
      Boolean(item[field])
    );
  };

  return (
    <>
      <Button variant='outlined' onClick={onOpen}>
        Добавить сайт отзывов
      </Button>

      <Dialog open={isOpen} onClose={onClose} fullWidth={true} maxWidth='sm'>
        <DialogTitle>Сайт отзывов</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 132,
              paddingBottom: 1,
            }}
          >
            {imageUrl && !isImageError ? (
              <Box
                alt={item.name || 'Feedback site'}
                component='img'
                onError={() => setIsImageError(true)}
                src={imageUrl}
                sx={{
                  borderRadius: 1,
                  maxHeight: 120,
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  alignItems: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  color: 'text.secondary',
                  display: 'flex',
                  height: 120,
                  justifyContent: 'center',
                  width: 180,
                }}
              >
                No image
              </Box>
            )}
          </Box>

          {NewFeedbackSiteFormConfig.map(({ label, field, required }, idx) => {
            return (
              <TextField
                key={field}
                autoFocus={idx === 0}
                margin='dense'
                id={field}
                label={label}
                fullWidth
                required={required}
                value={item[field]}
                onChange={changeInput(field)}
              />
            );
          })}

          <FormControlLabel
            control={
              <Checkbox
                checked={item.isDoctorFeedback}
                onChange={(event) => changeItem('isDoctorFeedback')(event.target.checked)}
              />
            }
            label='Отзывы врачей'
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onAddButtonClick} disabled={!isValidForm()}>
            {feedbackSite?.id ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddFeedbackSiteButton;
